import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { deleteReview, fetchMyReviews, updateReview } from '../../api/reviews';
import ReviewModal from '../../components/reviews/ReviewModal';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import DashboardShell from '../../components/ui/DashboardShell';
import PaginatedFooter from '../../components/ui/PaginatedFooter';
import { DEFAULT_PAGE_SIZE } from '../../constants/pagination';
import { useToast } from '../../context/ToastContext';

const ClientReviewsPage = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['my-reviews', page, pageSize],
    queryFn: () => fetchMyReviews({ page, limit: pageSize }),
  });

  const reviews = data?.data || [];
  const pagination = data?.pagination;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['my-reviews'] });
    queryClient.invalidateQueries({ queryKey: ['client-service-history'] });
    queryClient.invalidateQueries({ queryKey: ['client-order-history'] });
    queryClient.invalidateQueries({ queryKey: ['orders'] });
    queryClient.invalidateQueries({ queryKey: ['salon-reviews'] });
  };

  const updateMut = useMutation({
    mutationFn: ({ id, body }) => updateReview(id, body),
    onSuccess: () => {
      showToast(t('toast.reviewUpdated'), 'success');
      setEditTarget(null);
      invalidate();
    },
    onError: (err) => showToast(err.response?.data?.message || t('toast.errorGeneric'), 'error'),
  });

  const deleteMut = useMutation({
    mutationFn: deleteReview,
    onSuccess: () => {
      showToast(t('toast.reviewDeleted'), 'success');
      setDeleteTarget(null);
      invalidate();
    },
    onError: (err) => showToast(err.response?.data?.message || t('toast.errorGeneric'), 'error'),
  });

  const reviewLabel = (r) => {
    if (r.type === 'SERVICE') return r.subjectName || t('reviews.types.SERVICE');
    if (r.type === 'PRODUCT') return r.subjectName || t('reviews.types.PRODUCT');
    return r.salonName || r.subjectName || t('reviews.types.SALON');
  };

  return (
    <DashboardShell title={t('reviews.myReviewsTitle')} subtitle={t('reviews.myReviewsSubtitle')}>
      {isLoading && <p className="text-zinc-600 dark:text-zinc-500">{t('common.loading')}</p>}

      {!isLoading && reviews.length === 0 && (
        <p className="text-sm text-zinc-600 dark:text-zinc-500">{t('reviews.noMyReviews')}</p>
      )}

      <div className="space-y-4">
        {reviews.map((r) => (
          <div key={r._id} className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-5">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <span className="rounded-full bg-zinc-100 dark:bg-zinc-800 px-2 py-0.5 text-[10px] uppercase text-zinc-500">
                  {t(`reviews.types.${r.type}`)}
                </span>
                <p className="mt-1 font-medium">{reviewLabel(r)}</p>
                <p className="text-xs text-amber-400">{'★'.repeat(r.rating)}</p>
                <p className="mt-1 text-xs text-zinc-500">
                  {new Date(r.createdAt).toLocaleString()}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setEditTarget(r)}
                  className="text-xs text-glow-400 hover:underline"
                >
                  {t('common.edit')}
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteTarget(r)}
                  className="text-xs text-red-400 hover:underline"
                >
                  {t('common.delete')}
                </button>
              </div>
            </div>
            {r.comment && <p className="mt-3 text-sm text-zinc-600 dark:text-zinc-400">{r.comment}</p>}
            <p className="mt-2 text-[11px] text-zinc-500">
              {r.isVisible ? t('reviews.statusPublished') : t('reviews.statusPendingPublish')}
            </p>
            {r.reply?.message && (
              <div className="mt-3 rounded-lg border border-glow-800/30 bg-glow-950/20 p-3 text-sm">
                <p className="text-xs font-medium text-glow-300">{t('reviews.salonReply')}</p>
                <p className="mt-1 text-zinc-600 dark:text-zinc-400">{r.reply.message}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      <PaginatedFooter
        pagination={pagination}
        page={page}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
      />

      <ReviewModal
        open={!!editTarget}
        title={t('reviews.editReview')}
        subtitle={editTarget ? reviewLabel(editTarget) : ''}
        initialRating={editTarget?.rating || 0}
        initialComment={editTarget?.comment || ''}
        submitLabel={t('common.save')}
        onClose={() => setEditTarget(null)}
        saving={updateMut.isPending}
        onSubmit={({ rating, comment }) =>
          updateMut.mutate({ id: editTarget._id, body: { rating, comment } })
        }
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('reviews.deleteTitle')}
        message={t('reviews.deleteMessage')}
        confirmLabel={t('common.delete')}
        confirmVariant="danger"
        loading={deleteMut.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => deleteMut.mutate(deleteTarget._id)}
      />
    </DashboardShell>
  );
};

export default ClientReviewsPage;
